"use client";

import { useCallback, useEffect, useSyncExternalStore, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { lockPageScroll, unlockPageScroll } from "@/lib/scrollLock";
import { cn } from "@/lib/utils";
import type { ImageDataType } from "@/shared/types";
import type { GalleryControlsTheme } from "./types";
import { GalleryNavButton } from "./GalleryNavButton";
import { GalleryPagination } from "./GalleryPagination";
import { LightboxImageFrame } from "./LightboxImageFrame";

interface GalleryLightboxProps {
  images: ImageDataType[];
  initialIndex: number;
  onClose: () => void;
}

/** Light controls on dimmed overlay — always the same, regardless of section theme. */
const LIGHTBOX_CONTROLS_THEME: GalleryControlsTheme = {
  navButton: "bg-background text-foreground hover:opacity-80",
  paginationDotActive: "bg-background",
  paginationDotInactive: "border-background/60 bg-transparent",
};

function subscribeToClientRender() {
  return () => {};
}

function getPortalNode() {
  return document.body;
}

function getServerPortalNode(): HTMLElement | null {
  return null;
}

/** Site backdrop with dimming overlay — same pattern as menu and search. */
export function GalleryLightbox({
  images,
  initialIndex,
  onClose,
}: GalleryLightboxProps) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const portalNode = useSyncExternalStore(
    subscribeToClientRender,
    getPortalNode,
    getServerPortalNode
  );
  const hasMultipleImages = images.length > 1;
  const activeImage = images[activeIndex];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (!hasMultipleImages) {
        return;
      }

      if (event.key === "ArrowLeft") {
        setActiveIndex((currentIndex) =>
          currentIndex === 0 ? images.length - 1 : currentIndex - 1
        );
      }

      if (event.key === "ArrowRight") {
        setActiveIndex((currentIndex) =>
          currentIndex === images.length - 1 ? 0 : currentIndex + 1
        );
      }
    };

    lockPageScroll();
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockPageScroll();
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, hasMultipleImages, images.length]);

  const goToPrevious = useCallback(() => {
    setActiveIndex((currentIndex) =>
      currentIndex === 0 ? images.length - 1 : currentIndex - 1
    );
  }, [images.length]);

  const goToNext = useCallback(() => {
    setActiveIndex((currentIndex) =>
      currentIndex === images.length - 1 ? 0 : currentIndex + 1
    );
  }, [images.length]);

  const goToSlide = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  if (images.length === 0 || !portalNode || !activeImage) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-x-0 bottom-0 top-[var(--header-height)] z-[60] overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Просмотр изображения"
    >
      <button
        type="button"
        className="absolute inset-0 bg-secondary/50"
        aria-label="Закрыть просмотр"
        onClick={onClose}
      />

      <button
        type="button"
        onClick={onClose}
        aria-label="Закрыть"
        className={cn(
          "pointer-events-auto absolute right-4 top-4 z-20 flex size-12 shrink-0 items-center justify-center transition-opacity hover:opacity-80 md:right-10 md:top-6",
          LIGHTBOX_CONTROLS_THEME.navButton
        )}
      >
        <X className="size-6" strokeWidth={1.5} />
      </button>

      <div className="pointer-events-none relative flex h-full flex-col items-center justify-center px-4 pb-10 pt-20 md:px-10 md:pb-10 md:pt-24">
        <div className="pointer-events-auto relative z-10 flex w-full max-w-[1480px] items-center justify-center gap-6">
          {hasMultipleImages && (
            <GalleryNavButton
              direction="prev"
              ariaLabel="Предыдущее изображение"
              onClick={goToPrevious}
              themeClassName={LIGHTBOX_CONTROLS_THEME.navButton}
              className="hidden shrink-0 md:flex"
            />
          )}

          <div className="flex min-w-0 w-full max-w-[1360px] justify-center overflow-hidden">
            <LightboxImageFrame
              key={activeIndex}
              image={activeImage}
              priority
            />
          </div>

          {hasMultipleImages && (
            <GalleryNavButton
              direction="next"
              ariaLabel="Следующее изображение"
              onClick={goToNext}
              themeClassName={LIGHTBOX_CONTROLS_THEME.navButton}
              className="hidden shrink-0 md:flex"
            />
          )}
        </div>

        {hasMultipleImages && (
          <GalleryPagination
            total={images.length}
            activeIndex={activeIndex}
            onSelect={goToSlide}
            dotActiveClass={LIGHTBOX_CONTROLS_THEME.paginationDotActive}
            dotInactiveClass={LIGHTBOX_CONTROLS_THEME.paginationDotInactive}
            className="pointer-events-auto relative z-10 mt-6 md:hidden"
          />
        )}
      </div>
    </div>,
    portalNode
  );
}
